import {
  Box,
  Button,
  Divider,
  Skeleton,
  Tooltip,
  Typography,
} from "@mui/material";

import { CardMembership, Yard } from "@mui/icons-material";
import MainContentWithResponsiveDrawer from "../../common/components/data-display/MainContentWithResponsiveDrawer";
import AdvancedRequestsInSalesLog from "./AdvancedRequestsInSalesLog";
import PointOfSaleBaseDetailsForm from "./PointOfSaleBaseDetailsForm";
import PointOfSaleCart from "./PointOfSaleCart";
import { usePointOfSaleContext } from "./PointOfSaleContextProvider";
import PointOfSaleInventorySearch from "./PointOfSaleInventorySearch";
import PointOfSaleOtherItemsSearch from "./PointOfSaleOtherItemsSearch";
import PointOfSaleQuickSelectOptions from "./PointOfSaleQuickSelectOptions";
import PresaleOrdersInSalesLog from "./PresaleOrdersInSalesLog";
import SaleTypeSelectionPage from "./SaleTypeSelectionPage";
import { useCollective } from "../../common/context/CollectiveContext";
import { useEffect } from "react";
import { SalesLogType } from "../../common/enums/SalesLogEnums";

export default function PointOfSale({ isNew }) {
  const {
    salesLog,
    isShoppingInventory,
    setIsShoppingInventory,
    setBackToAdvancedRequest,
    setBackToPresaleOrder,
    isShoppingOther,
    setIsShoppingOther,
    isNew: contextIsNew,
    newSaleLogOfType,
  } = usePointOfSaleContext();
  const { collectiveInfo } = useCollective();

  const isNewCheckoutFlow = (contextIsNew && !salesLog) || isNew;
  const isRetailConfigured = Boolean(collectiveInfo?.retailMarkupPct);

  useEffect(() => {
    // If no retail rate is configured, set up a new sales log immediately of type Wholesale.
    if (isNewCheckoutFlow && !isRetailConfigured) {
      newSaleLogOfType(SalesLogType.Wholesale);
    }
  }, [isNewCheckoutFlow, isRetailConfigured, newSaleLogOfType]);

  // If we're in a new checkout flow and don't have a sales log yet, show the type selection page
  // if there is a choice to be made (i.e. retail is configured)
  if (isNewCheckoutFlow && isRetailConfigured) {
    return <SaleTypeSelectionPage onNext={newSaleLogOfType} />;
  }

  if (isNewCheckoutFlow) {
    // Render a placeholder while the new sales log is being set up
    return (
      <Box sx={{ height: "50vh" }}>
        <Skeleton variant="rectangular" height="100%" />
      </Box>
    );
  }

  const isBuyerSelected = Boolean(salesLog?.buyerBusiness);
  const saleDate = salesLog?.saleDate;
  return (
    <MainContentWithResponsiveDrawer
      mainContent={
        <Box data-testid="point-of-sale-content">
          <Typography variant="h4" gutterBottom sx={{ m: 2 }}>
            Sales Checkout
          </Typography>
          <Box sx={{ display: isShoppingInventory ? "block" : "none" }}>
            <PointOfSaleInventorySearch saleDate={saleDate} />
          </Box>
          <Box sx={{ display: isShoppingOther ? "block" : "none" }}>
            <PointOfSaleOtherItemsSearch />
          </Box>
          <Box
            sx={{
              display:
                isShoppingInventory || isShoppingOther ? "none" : "block",
            }}
          >
            <PointOfSaleBaseDetailsForm />
            <Divider />
            <PointOfSaleQuickSelectOptions />
            <Divider />
            <AdvancedRequestsInSalesLog />
            <PresaleOrdersInSalesLog />
            <Box
              display="flex"
              justifyContent={"center"}
              sx={{ mt: 2, width: "100%", gap: 2 }}
              flexWrap="wrap"
            >
              <Tooltip title={isBuyerSelected ? "" : "Select a buyer first"}>
                <div>
                  <Button
                    size="large"
                    onClick={() => {
                      setIsShoppingInventory(true);
                      // When coming from main menu, reset back button callbacks to hide them
                      setBackToAdvancedRequest(null);
                      setBackToPresaleOrder(null);
                    }}
                    variant="contained"
                    startIcon={<Yard />}
                    disabled={!isBuyerSelected}
                  >
                    Shop Floral Inventory
                  </Button>
                </div>
              </Tooltip>
              <Tooltip title={isBuyerSelected ? "" : "Select a buyer first"}>
                <div>
                  <Button
                    size="large"
                    onClick={() => {
                      setIsShoppingOther(true);
                      // When coming from main menu, reset back button callbacks to hide them
                      setBackToAdvancedRequest(null);
                      setBackToPresaleOrder(null);
                    }}
                    variant="contained"
                    startIcon={<CardMembership />}
                    disabled={!isBuyerSelected}
                  >
                    Shop Other Items
                  </Button>
                </div>
              </Tooltip>
            </Box>
          </Box>
        </Box>
      }
      drawerContent={<PointOfSaleCart view="shopping" />}
      mobileOpenDrawerContent={"View cart"}
    />
  );
}
